import React from "react";
import injectSheet from "react-jss";
import PropTypes from "prop-types";
require("core-js/fn/array/from");

import HomeIcon from "@material-ui/icons/Home";
import BlogIcon from "@material-ui/icons/ChromeReaderMode";

import Item from "./Item";
import Expand from "./Expand";

const styles = theme => ({
  menu: {
    alignItems: "center",
    backgroundColor: "white",
    bottom: 0,
    display: "flex",
    flexGrow: 1,
    left: 0,
    padding: `0 ${theme.spacing.S}px`,
    position: "fixed",
    width: "100%",
    zIndex: 1,
    transition: `all ${theme.time.duration.default}`,
    maxHeight: 50,
    "&.open": {
      maxHeight: 1000
    },
    [`@media (max-width: ${theme.mediaQueryTresholds.L - 1}px)`]: {
      "&:not(.subview)": {
        "&::after": {
          position: "absolute",
          content: "''",
          left: theme.spacing.M,
          right: theme.spacing.M,
          top: 0,
          height: 1,
          background: theme.palette.secondary.main
        }
      },
      "&.open": {
        padding: theme.spacing.M
      },
      "&:not(.fixed)": {
        bottom: -100
      }
    },
    [`@media (min-width: ${theme.mediaQueryTresholds.L}px)`]: {
      borderTop: "none",
      background: "transparent",
      display: "flex",
      position: "relative",
      justifyContent: "flex-end",
      paddingLeft: 50,
      transition: "none"
    },
    "&.subview": {
      borderTop: "none",
      background: "transparent",
      display: "flex",
      position: "relative",
      justifyContent: "flex-end",
      paddingLeft: 50,
      transition: "none",
      bottom: 0
    }
  },
  itemList: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    listStyle: "none",
    margin: 0,
    padding: 0,
    position: "relative",
    width: "100%",
    [`@media (max-width: ${theme.mediaQueryTresholds.L - 1}px)`]: {
      padding: 0,
      justifyContent: "flex-end",
      "&.homepage": {
        justifyContent: "center"
      }
    },
    [`@media (min-width: ${theme.mediaQueryTresholds.L}px)`]: {
      padding: 0,
      justifyContent: "flex-end"
    }
  },
  hiddenItemList: {
    [`@media (min-width: ${theme.mediaQueryTresholds.L}px)`]: {
      listStyle: "none",
      margin: 0,
      position: "absolute",
      background: theme.main.colors.background,
      border: `1px solid ${theme.base.colors.lines}`,
      top: 48,
      right: theme.spacing.S,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      padding: theme.spacing.M,
      borderRadius: theme.base.sizes.radius.small,
      borderTopRightRadius: 0,
      "&::after": {
        content: "''",
        background: theme.palette.secondary.main,
        zIndex: 10,
        top: -10,
        right: -1,
        width: 44,
        height: 10,
        position: "absolute",
        borderLeft: `1px solid ${theme.base.colors.lines}`,
        borderRight: `1px solid ${theme.base.colors.lines}`
      }
    }
  }
});

class Menu extends React.Component {
  constructor(props) {
    super(props);
    this.itemList = React.createRef();

    const pages = props.pages.map((page, index) => ({
      to: page.node.fields.slug,
      label: page.node.frontmatter.menuTitle
        ? page.node.frontmatter.menuTitle
        : page.node.frontmatter.title,
      index: index + 1
    }));

    this.items = [
      { to: "/", label: "Główna", icon: HomeIcon, index: this.props.isSubView ? undefined : 0 },
      ...pages,
      {
        to: "/blog/",
        label: this.props.isSubView ? "Spis" : "Blog",
        icon: BlogIcon,
        index: this.props.isSubView ? undefined : props.pages.length + 1
      }
    ];

    this.renderedItems = []; // will contain references to rendered DOM elements of menu
  }

  state = {
    open: false,
    hiddenItems: []
  };

  static propTypes = {
    path: PropTypes.string.isRequired,
    fixed: PropTypes.bool.isRequired,
    isHomepage: PropTypes.bool.isRequired,
    isWideScreen: PropTypes.bool.isRequired,
    pages: PropTypes.array.isRequired,
    classes: PropTypes.object.isRequired,
    onClickItem: PropTypes.func,
    isSubView: PropTypes.bool
  };

  componentDidMount() {
    this.renderedItems = this.getRenderedItems();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.path !== prevProps.path ||
      this.props.fixed !== prevProps.fixed ||
      this.props.isWideScreen !== prevProps.isWideScreen
    ) {
      if (this.props.path !== prevProps.path) {
        this.closeMenu();
      }
      this.hideOverflowedMenuItems();
    }
  }

  getRenderedItems = () => {
    const itemList = this.itemList.current;
    return Array.from(itemList.children);
  };

  hideOverflowedMenuItems = () => {
    const PADDING_AND_SPACE_FOR_MORELINK = this.props.isWideScreen ? 60 : 0;

    const itemsContainer = this.itemList.current;
    const maxWidth = itemsContainer.offsetWidth - PADDING_AND_SPACE_FOR_MORELINK;

    this.setState({ hiddenItems: [] }); // clears previous state

    const menu = this.renderedItems.reduce(
      (result, item) => {
        item.classList.add("item");
        item.classList.remove("hideItem");

        const currentCumulativeWidth = result.cumulativeWidth + item.offsetWidth;
        result.cumulativeWidth = currentCumulativeWidth;

        if (!item.classList.contains("more") && currentCumulativeWidth > maxWidth) {
          item.classList.add("hideItem");
          item.classList.remove("item");
          const button = item.querySelector("button");
          if (button) {
            result.hiddenItems.push({
              to: button.getAttribute("data-slug"),
              label: button.text
            });
          }
        }
        return result;
      },
      { visibleItems: [], cumulativeWidth: 0, hiddenItems: [] }
    );

    this.setState(prevState => ({ hiddenItems: menu.hiddenItems }));
  };

  toggleMenu = e => {
    e.preventDefault();

    if (!this.props.isWideScreen) {
      this.renderedItems.map(item => {
        const oldClass = this.state.open ? "showItem" : "hideItem";
        const newClass = this.state.open ? "hideItem" : "showItem";

        if (item.classList.contains(oldClass)) {
          item.classList.add(newClass);
          item.classList.remove(oldClass);
        }
      });
    }

    this.setState(prevState => ({ open: !prevState.open }));
  };

  closeMenu = e => {
    //e.preventDefault();

    if (this.state.open) {
      this.setState({ open: false });
      if (!this.props.isWideScreen) {
        this.renderedItems.map(item => {
          if (item.classList.contains("showItem")) {
            item.classList.add("hideItem");
            item.classList.remove("item");
          }
        });
      }
    }
  };

  handleClickItem = index => () => {
    this.props.onClickItem(index);
  };

  render() {
    const { isWideScreen, fixed, isHomepage, classes } = this.props;
    const { open } = this.state;

    return (
      <React.Fragment>
        <nav
          className={`${classes.menu} ${open ? "open" : ""} ${fixed ? "fixed" : ""} ${
            this.props.isSubView ? "subview" : ""
          }`}
          rel="js-menu"
        >
          <ul className={`${classes.itemList} ${isHomepage ? "homepage" : ""}`} ref={this.itemList}>
            {this.items.map(item => (
              <Item
                item={item}
                key={item.label}
                icon={item.icon}
                onClick={item.index !== undefined ? this.handleClickItem(item.index) : undefined}
              />
            ))}
          </ul>
          {this.state.hiddenItems.length > 0 && (
            <Expand onClick={this.toggleMenu} expanded={open} />
          )}
          {open &&
            isWideScreen && (
              <ul className={classes.hiddenItemList}>
                {this.state.hiddenItems.map(item => (
                  <Item item={item} key={item.label} hiddenItem />
                ))}
              </ul>
            )}
        </nav>
      </React.Fragment>
    );
  }
}

export default injectSheet(styles)(Menu);



// WEBPACK FOOTER //
// ./src/components/Menu/Menu.js